import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { WaterfallVisualization } from "../components/WaterfallVisualization";
import { MarkdownRenderer } from "../components/MarkdownRenderer";
import { ChatClient } from "../components/ChatClient";
import { ResizablePanel } from "../components/ResizablePanel";
import { useWorkflow } from "../contexts/WorkflowContext";
import { Event } from "../../shared/types";

export const WorkflowView: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const {
    workflow,
    agents,
    events,
    isConnected,
    isRunning,
    connectWebSocket,
    startWorkflow,
    stopWorkflow,
    sendChatMessage,
    loading,
    error,
    setError,
  } = useWorkflow();

  const [zoomX, setZoomX] = useState(100);
  const [zoomY, setZoomY] = useState(100);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [chatWidth, setChatWidth] = useState(320);
  const waterfallRef = useRef<{ centralizeEvent: (eventId: string) => void } | null>(null);

  // Connect WebSocket when workflow is loaded
  useEffect(() => {
    if (workflow?.id && workflowId) {
      connectWebSocket(workflowId);
    }
  }, [workflow?.id, workflowId, connectWebSocket]);

  // Workflow will remain idle until user sends first message or manually starts it

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
  };

  const getCurrentTime = () => {
    if (events.length === 0) return 0;
    return Math.max(...events.map((e) => e.timestamp));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "text-green-500";
      case "completed":
        return "text-blue-500";
      case "failed":
        return "text-red-500";
      case "idle":
        return "text-gray-500";
      default:
        return "text-gray-400";
    }
  };

  const handleAgentClick = (agentId: string) => {
    navigate(`/app/workflow/${workflowId}/agent/${agentId}`);
  };

  const handleChatMessageSelect = useCallback((eventId: string) => {
    // First show the event details
    const event = events.find(e => e.id === eventId);
    if (event) {
      setSelectedEvent(event);
    }
    
    // Then center the event in the waterfall
    if (waterfallRef.current) {
      waterfallRef.current.centralizeEvent(eventId);
    }
  }, [events]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-300">Loading workflow...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">Error</div>
          <p className="text-gray-300 mb-4">{error}</p>
          <button
            onClick={() => navigate('/app')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded"
          >
            Back to Workflows
          </button>
        </div>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 text-lg mb-4">No workflow data</div>
          <button
            onClick={() => navigate('/app')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded"
          >
            Back to Workflows
          </button>
        </div>
      </div>
    );
  }

  const currentTime = getCurrentTime();

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Workflow Header */}
      <div className="flex-shrink-0 bg-black bg-opacity-50 backdrop-blur-sm border-b border-border-primary p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div
                className={`w-2 h-2 rounded-full ${isConnected ? "bg-green-500" : "bg-red-500"}`}
              />
              <span className="text-sm text-gray-300">
                {isConnected ? "Connected" : "Disconnected"}
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-1">
                <h1 className="text-lg font-semibold text-white">
                  {workflow.name}
                </h1>
                <p className="text-sm text-gray-300 max-w-md truncate">
                  {workflow.objective}
                </p>
              </div>
              
              <div
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(workflow.status || "idle")}`}
              >
                {(workflow.status || "idle").toUpperCase()}
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3">
            {events.length > 0 && (
              <>
                <div className="text-sm text-gray-300 font-mono">
                  Live: {currentTime.toFixed(1)}s
                </div>

                <div className="flex items-center gap-1 text-xs">
                  <span className="text-gray-400">Time:</span>
                  <input
                    type="range"
                    min="50"
                    max="300"
                    step="10"
                    value={zoomX}
                    onChange={(e) => setZoomX(parseInt(e.target.value))}
                    className="w-12 h-1"
                    title="Zoom timeline (trackpad: shift+scroll)"
                  />
                  <span className="text-gray-300 w-8 text-right">{zoomX}%</span>
                </div>

                <div className="flex items-center gap-1 text-xs">
                  <span className="text-gray-400">Rows:</span>
                  <input
                    type="range"
                    min="25"
                    max="200"
                    step="10"
                    value={zoomY}
                    onChange={(e) => setZoomY(parseInt(e.target.value))}
                    className="w-12 h-1"
                    title="Zoom row height (trackpad: scroll)"
                  />
                  <span className="text-gray-300 w-8 text-right">{zoomY}%</span>
                </div>
              </>
            )}

            {isRunning && (
              <button
                onClick={stopWorkflow}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Stop
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area - Side by Side Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Waterfall Visualization */}
        <div className="flex-1 relative overflow-hidden">
          <WaterfallVisualization
            ref={waterfallRef}
            events={events}
            agents={agents}
            currentTime={currentTime}
            zoomX={zoomX}
            zoomY={zoomY}
            onZoomXChange={setZoomX}
            onZoomYChange={setZoomY}
            onEventClick={handleEventClick}
            onSendMessage={sendChatMessage}
            onAgentClick={handleAgentClick}
            showChatClient={false}
            className="absolute inset-0"
          />

          {/* Event Details Overlay */}
          {selectedEvent && (
            <div className="absolute bottom-4 left-4 right-4 max-w-2xl mx-auto bg-bg-card bg-opacity-95 backdrop-blur-sm border border-border-primary rounded-lg p-6 z-40">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-white">
                  Event Details
                </h3>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-400">Type:</span>
                    <span className="ml-2 px-2 py-1 rounded text-xs bg-primary-600 text-white">
                      {selectedEvent.type}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-400">Agent:</span>
                    <span className="ml-2 text-white">
                      {agents.find((a) => a.id === selectedEvent.agentId)?.type || "Unknown"}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-400">Time:</span>
                    <span className="ml-2 text-white font-mono">
                      {selectedEvent.timestamp.toFixed(2)}s
                    </span>
                  </div>
                  {typeof selectedEvent.metadata?.toolName === "string" && (
                    <div>
                      <span className="text-sm text-gray-400">Tool:</span>
                      <span className="ml-2 text-white font-mono">
                        {String(selectedEvent.metadata.toolName)}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <span className="text-sm text-gray-400">Content:</span>
                  <div className="mt-1 max-h-48 overflow-y-auto bg-gray-800/50 p-3 rounded border border-gray-600">
                    <MarkdownRenderer
                      content={selectedEvent.content}
                      compact={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chat Client - Resizable Sidebar */}
        <ResizablePanel
          defaultWidth={chatWidth}
          minWidth={250}
          maxWidth={600}
          position="right"
          onWidthChange={setChatWidth}
          className="flex-shrink-0 bg-gray-900"
        >
          <ChatClient
            events={events}
            agents={agents}
            onSendMessage={sendChatMessage}
            onMessageSelect={handleChatMessageSelect}
            className="h-full"
          />
        </ResizablePanel>
      </div>

      {/* Toast notifications */}
      {notice && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className="bg-green-700 text-white text-sm px-3 py-2 rounded shadow-lg border border-green-500">
            {notice}
          </div>
        </div>
      )}
    </div>
  );
};
